import { APIError } from '../utils/errors.js';

export class BaseAPIService {
	constructor(config = {}) {
		this.workspace = config.workspace;
		this.sessionToken = null;
		this.sessionExpiry = null;
		this.userContext = config.userContext || null;
		this.mock = config.mock || false;
		this.env = config.env || 'production';
		this.baseURL = this._getBaseURL(config);

		this._loadStoredSession();
	}

	_getBaseURL(config) {
		if (config.apiUrl) return config.apiUrl;

		const ENV_URLS = {
			production: {
				base: 'https://api.product7.io/api/v1',
				withWorkspace: (ws) => `https://${ws}.api.product7.io/api/v1`,
			},
			staging: {
				base: 'https://staging.api.product7.io/api/v1',
				withWorkspace: (ws) => `https://${ws}.staging.api.product7.io/api/v1`,
			},
			localstack: {
				base: 'http://localhost:1323/api/v1',
				withWorkspace: (ws) => `http://${ws}.localhost:1323/api/v1`,
			},
		};

		const envConfig = ENV_URLS[this.env] || ENV_URLS.production;
		return this.workspace
			? envConfig.withWorkspace(this.workspace)
			: envConfig.base;
	}

	async init(userContext = null) {
		if (userContext) {
			this.userContext = userContext;
		}

		if (this.isSessionValid()) {
			return { sessionToken: this.sessionToken };
		}

		if (!this.workspace || !this.userContext) {
			throw new APIError(
				400,
				`Missing ${!this.workspace ? 'workspace' : 'user context'} for initialization`
			);
		}

		if (this.mock) {
			return this._initMockSession();
		}

		return this._initRealSession();
	}

	async _initMockSession() {
		this.sessionToken = 'mock_session_' + Date.now();
		this.sessionExpiry = new Date(Date.now() + 3600 * 1000);
		this._storeSession();
		return {
			sessionToken: this.sessionToken,
			config: {
				primaryColor: '#21244A',
				backgroundColor: '#ffffff',
				textColor: '#1F2937',
				boardId: 'feature-requests',
				size: 'medium',
				displayMode: 'modal',
			},
			expiresIn: 3600,
		};
	}

	async _initRealSession() {
		const payload = {
			workspace: this.workspace,
			user: this.userContext,
		};

		try {
			const response = await this._makeRequest('/widget/init', {
				method: 'POST',
				body: JSON.stringify(payload),
				headers: { 'Content-Type': 'application/json' },
			});
			const initData = this._extractInitResponseData(response);

			this.sessionToken = initData.sessionToken;
			this.sessionExpiry = new Date(Date.now() + initData.expiresIn * 1000);
			this._storeSession();

			return {
				sessionToken: this.sessionToken,
				config: initData.config,
				expiresIn: initData.expiresIn,
				status: initData.status,
				message: initData.message,
				configVersion: initData.configVersion,
			};
		} catch (error) {
			throw new APIError(
				error.status || 500,
				`Failed to initialize widget: ${error.message}`,
				error.response
			);
		}
	}

	_extractInitResponseData(response) {
		const payload =
			response && typeof response.data === 'object' ? response.data : response || {};

		const sessionToken = payload.session_token || payload.sessionToken;
		const expiresIn = Number(payload.expires_in ?? payload.expiresIn);

		if (!sessionToken) {
			throw new APIError(500, 'Invalid init response: missing session_token');
		}

		if (!Number.isFinite(expiresIn) || expiresIn <= 0) {
			throw new APIError(500, 'Invalid init response: missing expires_in');
		}

		return {
			sessionToken,
			expiresIn,
			config:
				payload.config && typeof payload.config === 'object'
					? payload.config
					: {},
			configVersion: payload.config_version ?? payload.configVersion ?? null,
			status: response?.status ?? payload?.status ?? true,
			message: response?.message ?? payload?.message ?? null,
		};
	}

	async _ensureSession() {
		if (!this.isSessionValid()) {
			await this.init();
		}
		if (!this.sessionToken) {
			throw new APIError(401, 'No valid session token available');
		}
	}

	async _handleAuthRetry(method, ...args) {
		try {
			return await method.apply(this, args);
		} catch (error) {
			if (error.status === 401) {
				this.sessionToken = null;
				this.sessionExpiry = null;
				await this.init();
				return await method.apply(this, args);
			}
			throw error;
		}
	}

	isSessionValid() {
		return (
			this.sessionToken && this.sessionExpiry && new Date() < this.sessionExpiry
		);
	}

	setUserContext(userContext) {
		this.userContext = userContext;
		this._storeData('feedbackSDK_userContext', userContext);
	}

	getUserContext() {
		return this.userContext;
	}

	clearSession() {
		this.sessionToken = null;
		this.sessionExpiry = null;
		this._removeData('feedbackSDK_session');
		this._removeData('feedbackSDK_userContext');
	}

	_storeSession() {
		if (typeof localStorage === 'undefined') return;
		try {
			const sessionData = {
				token: this.sessionToken,
				expiry: this.sessionExpiry.toISOString(),
				workspace: this.workspace,
			};
			this._storeData('feedbackSDK_session', sessionData);
		} catch (error) {
			// Silent fail
		}
	}

	_loadStoredSession() {
		if (typeof localStorage === 'undefined') return false;
		try {
			const stored = localStorage.getItem('feedbackSDK_session');
			if (!stored) return false;

			const sessionData = JSON.parse(stored);
			this.sessionToken = sessionData.token;
			this.sessionExpiry = new Date(sessionData.expiry);

			return this.isSessionValid();
		} catch (error) {
			return false;
		}
	}

	_storeData(key, value) {
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(key, JSON.stringify(value));
		}
	}

	_removeData(key) {
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem(key);
		}
	}

	async _makeRequest(endpoint, options = {}) {
		const url = `${this.baseURL}${endpoint}`;

		try {
			const response = await fetch(url, options);

			if (!response.ok) {
				let errorMessage = `HTTP ${response.status}`;
				let responseData = null;

				try {
					responseData = await response.json();
					errorMessage =
						responseData.message || responseData.error || errorMessage;
				} catch (e) {
					errorMessage = (await response.text()) || errorMessage;
				}

				throw new APIError(response.status, errorMessage, responseData);
			}

			const contentType = response.headers.get('content-type');
			if (contentType && contentType.includes('application/json')) {
				return await response.json();
			}

			return await response.text();
		} catch (error) {
			if (error instanceof APIError) throw error;
			throw new APIError(0, error.message, null);
		}
	}

	_buildQueryParams(params) {
		const queryParams = new URLSearchParams();
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				queryParams.append(
					key,
					typeof value === 'object' ? JSON.stringify(value) : value
				);
			}
		});
		return queryParams.toString();
	}

	_getEndpointWithParams(endpoint, params) {
		const queryString = this._buildQueryParams(params);
		return `${endpoint}${queryString ? '?' + queryString : ''}`;
	}
}
